const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? '';

class BlogService {
  constructor() {
    this.baseURL = `${BACKEND_URL}/api/blog`;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        const detail = typeof data === 'object' ? (data.detail || data.message || JSON.stringify(data)) : String(data);
        throw new Error(detail);
      }

      // Some endpoints wrap in { success, data }, others return data directly
      if (data && typeof data === 'object' && 'success' in data && !data.success) {
        throw new Error(data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('Blog service error:', error);
      throw error;
    }
  }

  // Blog Post methods
  async createPost(postData) {
    return this.request('/posts', {
      method: 'POST',
      body: JSON.stringify(postData),
    });
  }

  async getPosts(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category_id) params.append('category_id', filters.category_id);
    if (filters.tag_id) params.append('tag_id', filters.tag_id);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    return this.request(`/posts?${params.toString()}`);
  }

  async getPost(postId) {
    return this.request(`/posts/${postId}`);
  }

  async getPostBySlug(slug) {
    return this.request(`/posts/slug/${slug}`);
  }

  async updatePost(postId, postData) {
    return this.request(`/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(postData),
    });
  }

  async deletePost(postId) {
    return this.request(`/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  // Category methods
  async createCategory(categoryData) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async getCategories() {
    return this.request('/categories');
  }

  async getCategory(categoryId) {
    return this.request(`/categories/${categoryId}`);
  }

  // Tag methods
  async createTag(tagData) {
    return this.request('/tags', {
      method: 'POST',
      body: JSON.stringify(tagData),
    });
  }

  async getTags() {
    return this.request('/tags');
  }

  async getTag(tagId) {
    return this.request(`/tags/${tagId}`);
  }

  // Comment methods
  async createComment(postId, commentData) {
    return this.request(`/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify(commentData),
    });
  }

  async getComments(postId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    return this.request(`/posts/${postId}/comments?${params.toString()}`);
  }

  async updateCommentStatus(commentId, status) {
    const token = localStorage.getItem('adminToken');
    return this.request(`/comments/${commentId}/status?status=${status}`, {
      method: 'PUT',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  }
}

export default new BlogService();